/**
 * LayerItem Component
 *
 * Single layer row with visibility checkbox, format badge, and admin menu.
 * Uses Mantine Checkbox, Badge, Menu, ActionIcon.
 */

import { memo, useCallback, useState } from 'react';
import {
  Checkbox,
  Badge,
  Menu,
  ActionIcon,
  UnstyledButton,
  Text,
} from '@mantine/core';
import { MoreVertical, Pencil, Trash2, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { MapLayer, MapLayerFormat } from '@/types/layers';
import classes from './LayerItem.module.css';

const FORMAT_COLORS: Record<MapLayerFormat, string> = {
  GeoJson: 'blue',
  Kml: 'teal',
  Kmz: 'cyan',
  Gpx: 'orange',
  GeoPackage: 'grape',
  Shapefile: 'green',
};

interface LayerItemProps {
  layer: MapLayer;
  isVisible: boolean;
  onToggle: () => void;
  isAdmin: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
  onDownload?: () => void;
  /** Flush, hover-free row for plain list contexts (e.g. the share form). */
  plain?: boolean;
}

export const LayerItem = memo(function LayerItem({
  layer,
  isVisible,
  onToggle,
  isAdmin,
  onEdit,
  onDelete,
  onDownload,
  plain = false,
}: LayerItemProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const handleCheckboxChange = useCallback(() => {
    onToggle();
  }, [onToggle]);

  return (
    <div className={plain ? classes.itemPlain : classes.item}>
      <div className={classes.header}>
        <Checkbox
          checked={isVisible}
          onChange={handleCheckboxChange}
          label={layer.name}
        />

        <div className={classes.actions}>
          <Badge
            size="xs"
            variant="light"
            color={FORMAT_COLORS[layer.sourceFormat]}
            className={classes.formatBadge}
          >
            {t(`layers.formats.${layer.sourceFormat}`)}
          </Badge>

          {isAdmin && (
            <Menu position="bottom-end" withinPortal shadow="md">
              <Menu.Target>
                <ActionIcon
                  variant="subtle"
                  size="sm"
                  color="gray"
                  className={classes.menuTrigger}
                >
                  <MoreVertical size={14} />
                </ActionIcon>
              </Menu.Target>

              <Menu.Dropdown>
                <Menu.Item leftSection={<Pencil size={14} />} onClick={onEdit}>
                  {t('layers.actions.edit')}
                </Menu.Item>
                <Menu.Item
                  leftSection={<Download size={14} />}
                  onClick={onDownload}
                >
                  {t('layers.actions.download')}
                </Menu.Item>
                <Menu.Divider />
                <Menu.Item
                  leftSection={<Trash2 size={14} />}
                  color="red"
                  onClick={onDelete}
                >
                  {t('layers.actions.delete')}
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </div>
      </div>

      {layer.description && (
        <div className={classes.description}>
          <Text size="xs" c="dimmed" lineClamp={expanded ? undefined : 2}>
            {layer.description}
          </Text>
          {layer.description.length > 80 && (
            <UnstyledButton
              className={classes.descriptionToggle}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? t('common.showLess') : t('common.showMore')}
            </UnstyledButton>
          )}
        </div>
      )}
    </div>
  );
});
